/**
 * DataFreshnessPill — small "Synced Xh ago" pill in the dashboard chrome.
 *
 * Reads from the new `/api/govdata/sync-status/` endpoint (Phase M.M3).
 * Renders a clickable pill that opens a modal listing per-dataset sync
 * timestamps and row counts when the leader wants to see the breakdown.
 *
 * Phase E3 of issue #90 v3 plan.
 */
import { useEffect, useState } from 'react'
import { Clock } from 'lucide-react'
import { api } from '../../services/api'

interface DatasetStatus {
  name: string
  last_sync_at: string | null
  last_download_at?: string | null
  last_dbt_run_at?: string | null
  row_count?: number | null
  notes?: string | null
}

interface SyncStatus {
  last_sync_at: string | null
  datasets: DatasetStatus[]
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'unknown'
  const t = new Date(iso).getTime()
  const now = Date.now()
  const diffMs = now - t
  const min = diffMs / 60_000
  if (min < 1) return 'just now'
  if (min < 60) return `${Math.round(min)}m ago`
  const hr = min / 60
  if (hr < 48) return `${Math.round(hr)}h ago`
  const days = hr / 24
  if (days < 30) return `${Math.round(days)}d ago`
  return new Date(iso).toLocaleDateString()
}

export default function DataFreshnessPill() {
  const [status, setStatus] = useState<SyncStatus | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    api
      .getRaw<SyncStatus>('/api/govdata/sync-status/')
      .then(setStatus)
      .catch(() => setStatus(null))
  }, [])

  if (!status) return null

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Click to see per-dataset sync details"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
      >
        <Clock size={10} />
        Synced {formatRelative(status.last_sync_at)}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Data Freshness
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Most recent sync: {formatRelative(status.last_sync_at)}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-800">
                    <th className="py-1 pr-2 font-medium">Dataset</th>
                    <th className="py-1 pr-2 font-medium">Synced</th>
                    <th className="py-1 text-right font-medium">Rows</th>
                  </tr>
                </thead>
                <tbody>
                  {status.datasets.map((d) => (
                    <tr key={d.name} className="border-b border-gray-50 dark:border-gray-900">
                      <td className="py-1 pr-2 text-gray-700 dark:text-gray-300">{d.name}</td>
                      <td className="py-1 pr-2 text-gray-500">{formatRelative(d.last_sync_at)}</td>
                      <td className="py-1 text-right text-gray-500 font-mono">
                        {d.row_count?.toLocaleString() ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-right">
              <button
                onClick={() => setOpen(false)}
                className="text-xs px-3 py-1 rounded text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
